import { useState, useCallback } from 'react';
import {
  Sparkles,
  ArrowRight,
  Rocket,
  Loader2,
  CheckCircle2,
  XCircle,
  FileCode2,
  Zap,
  Shield,
  Coins,
  Vote,
  ImageIcon,
  X,
} from 'lucide-react';
import { useAiDeployPipeline, type PipelineStage } from '@/hooks/useAiDeployPipeline';
import { cn } from '@/lib/utils';

/* ================================================================== */
/*  Quick contract templates                                          */
/* ================================================================== */

interface QuickTemplate {
  id: string;
  label: string;
  icon: React.ElementType;
  prompt: string;
  color: string;
}

const TEMPLATES: QuickTemplate[] = [
  {
    id: 'erc20',
    label: 'ERC-20 Token',
    icon: Coins,
    prompt: 'Create an ERC-20 token contract with minting, burning, and pausable functionality. Include an owner role that can mint and pause.',
    color: 'text-yellow-400',
  },
  {
    id: 'erc721',
    label: 'NFT Collection',
    icon: ImageIcon,
    prompt: 'Create an ERC-721 NFT collection contract with metadata URI, minting with max supply, and royalty support (ERC-2981).',
    color: 'text-purple-400',
  },
  {
    id: 'dao',
    label: 'DAO Governor',
    icon: Vote,
    prompt: 'Create a DAO governance contract using OpenZeppelin Governor pattern with proposal creation, voting, and timelock execution.',
    color: 'text-blue-400',
  },
  {
    id: 'defi',
    label: 'DeFi Vault',
    icon: Shield,
    prompt: 'Create an ERC-4626 tokenized vault contract for a DeFi yield strategy with deposit, withdraw, and yield accounting.',
    color: 'text-green-400',
  },
  {
    id: 'custom',
    label: 'Custom',
    icon: Sparkles,
    prompt: '',
    color: 'text-orange-400',
  },
];

/* ================================================================== */
/*  Stage progress config                                             */
/* ================================================================== */

const STAGE_INFO: Record<PipelineStage, { label: string; icon: React.ElementType; color: string }> = {
  idle: { label: 'Ready', icon: Sparkles, color: 'text-[#888]' },
  generating: { label: 'AI Generating', icon: Sparkles, color: 'text-purple-400' },
  'creating-file': { label: 'Creating File', icon: FileCode2, color: 'text-blue-400' },
  compiling: { label: 'Compiling', icon: Zap, color: 'text-yellow-400' },
  fixing: { label: 'Auto-Fixing', icon: Shield, color: 'text-orange-400' },
  deploying: { label: 'Deploying', icon: Rocket, color: 'text-green-400' },
  complete: { label: 'Deployed!', icon: CheckCircle2, color: 'text-green-400' },
  error: { label: 'Error', icon: XCircle, color: 'text-red-400' },
};

/* ================================================================== */
/*  Component                                                         */
/* ================================================================== */

interface QuickStartWizardProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function QuickStartWizard({ isOpen, onClose }: QuickStartWizardProps) {
  const [step, setStep] = useState<'choose' | 'describe' | 'running' | 'done'>('choose');
  const [selectedTemplate, setSelectedTemplate] = useState<QuickTemplate | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const { state, run, reset } = useAiDeployPipeline();

  const handleSelectTemplate = useCallback((tpl: QuickTemplate) => {
    setSelectedTemplate(tpl);
    if (tpl.id === 'custom') {
      setStep('describe');
    } else {
      setCustomPrompt(tpl.prompt);
      setStep('describe');
    }
  }, []);

  const handleRun = useCallback(async () => {
    const prompt = customPrompt.trim();
    if (!prompt) return;
    setStep('running');
    await run(prompt);
    setStep('done');
  }, [customPrompt, run]);

  const handleClose = useCallback(() => {
    reset();
    setStep('choose');
    setSelectedTemplate(null);
    setCustomPrompt('');
    onClose();
  }, [onClose, reset]);

  const handleBack = useCallback(() => {
    if (step === 'describe') {
      setStep('choose');
      setSelectedTemplate(null);
      setCustomPrompt('');
    }
  }, [step]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-[#1e1e1e] border border-[#333] rounded-xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-[#666] hover:text-white transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-[#333]">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500">
              <Rocket className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Quick Start</h2>
              <p className="text-[10px] text-[#888]">From idea to deployed contract in minutes</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Choose template */}
          {step === 'choose' && (
            <div className="space-y-3">
              <p className="text-xs text-[#aaa] mb-4">What do you want to build?</p>
              <div className="grid grid-cols-2 gap-2">
                {TEMPLATES.map((tpl) => {
                  const Icon = tpl.icon;
                  return (
                    <button
                      key={tpl.id}
                      onClick={() => handleSelectTemplate(tpl)}
                      className={cn(
                        'flex items-center gap-2.5 p-3 rounded-lg border border-[#333] transition-all text-left',
                        'hover:border-[#555] hover:bg-[#252526]',
                        tpl.id === 'custom' && 'col-span-2',
                      )}
                    >
                      <Icon className={cn('w-5 h-5 shrink-0', tpl.color)} />
                      <div>
                        <div className="text-xs font-medium text-white">{tpl.label}</div>
                        {tpl.id !== 'custom' && (
                          <div className="text-[9px] text-[#666] mt-0.5 line-clamp-1">{tpl.prompt.slice(0, 60)}...</div>
                        )}
                        {tpl.id === 'custom' && (
                          <div className="text-[9px] text-[#666] mt-0.5">Describe your contract in plain English</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Describe / Customize prompt */}
          {step === 'describe' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button onClick={handleBack} className="text-[10px] text-[#888] hover:text-white transition-colors">
                  ← Back
                </button>
                {selectedTemplate && selectedTemplate.id !== 'custom' && (
                  <span className={cn('text-xs font-medium', selectedTemplate.color)}>
                    {selectedTemplate.label}
                  </span>
                )}
              </div>

              <div>
                <label className="text-[10px] text-[#888] block mb-1.5">
                  Describe your smart contract
                </label>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="e.g., Create an ERC-20 token called 'MyToken' with symbol 'MTK', 1 million total supply, and a 2% transfer fee..."
                  rows={4}
                  className="w-full px-3 py-2 bg-[#252526] border border-[#444] rounded-lg text-xs text-white placeholder-[#555] focus:border-purple-500 focus:outline-none resize-none"
                  autoFocus
                />
              </div>

              <button
                onClick={handleRun}
                disabled={!customPrompt.trim()}
                className={cn(
                  'flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-xs font-semibold transition-all',
                  customPrompt.trim()
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white shadow-lg shadow-purple-500/20'
                    : 'bg-[#333] text-[#666] cursor-not-allowed',
                )}
              >
                <Sparkles className="w-4 h-4" />
                Generate, Compile & Deploy
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Step 3: Running pipeline */}
          {(step === 'running' || step === 'done') && (
            <PipelineProgress state={state} onClose={handleClose} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Pipeline Progress UI                                              */
/* ================================================================== */

function PipelineProgress({
  state,
  onClose,
}: {
  state: ReturnType<typeof useAiDeployPipeline>['state'];
  onClose: () => void;
}) {
  const stageInfo = STAGE_INFO[state.stage];
  const StageIcon = stageInfo.icon;
  const isRunning = !['complete', 'error', 'idle'].includes(state.stage);

  return (
    <div className="space-y-5">
      {/* Progress bar */}
      <div className="relative h-1.5 bg-[#333] rounded-full overflow-hidden">
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out',
            state.stage === 'error' ? 'bg-red-500' : 'bg-gradient-to-r from-purple-500 to-blue-500',
          )}
          style={{ width: `${state.progress}%` }}
        />
        {isRunning && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse" />
        )}
      </div>

      {/* Stage indicator */}
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-full',
            state.stage === 'complete'
              ? 'bg-green-500/20'
              : state.stage === 'error'
                ? 'bg-red-500/20'
                : 'bg-purple-500/20',
          )}
        >
          {isRunning ? (
            <Loader2 className={cn('w-5 h-5 animate-spin', stageInfo.color)} />
          ) : (
            <StageIcon className={cn('w-5 h-5', stageInfo.color)} />
          )}
        </div>
        <div>
          <div className={cn('text-sm font-semibold', stageInfo.color)}>{stageInfo.label}</div>
          <div className="text-[10px] text-[#888]">{state.message}</div>
        </div>
        <div className="ml-auto text-[10px] text-[#555]">{state.progress}%</div>
      </div>

      {/* Pipeline steps visual */}
      <div className="flex items-center justify-between px-2">
        {(['generating', 'creating-file', 'compiling', 'deploying'] as PipelineStage[]).map((stage, i) => {
          const info = STAGE_INFO[stage];
          const Icon = info.icon;
          const currentIdx = ['generating', 'creating-file', 'compiling', 'fixing', 'deploying', 'complete'].indexOf(state.stage);
          const stageIdx = ['generating', 'creating-file', 'compiling', 'deploying'].indexOf(stage);
          const isDone = currentIdx > stageIdx + (stage === 'compiling' ? 1 : 0);
          const isCurrent = state.stage === stage || (state.stage === 'fixing' && stage === 'compiling');

          return (
            <div key={stage} className="flex items-center">
              <div
                className={cn(
                  'flex items-center justify-center w-7 h-7 rounded-full border transition-all',
                  isDone
                    ? 'border-green-500 bg-green-500/20'
                    : isCurrent
                      ? 'border-purple-500 bg-purple-500/20'
                      : 'border-[#444] bg-[#252526]',
                )}
              >
                {isDone ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
                ) : (
                  <Icon
                    className={cn(
                      'w-3.5 h-3.5',
                      isCurrent ? cn(info.color, 'animate-pulse') : 'text-[#555]',
                    )}
                  />
                )}
              </div>
              {i < 3 && (
                <div
                  className={cn(
                    'w-8 h-px mx-1',
                    isDone ? 'bg-green-500' : 'bg-[#333]',
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Contract name */}
      {state.contractName && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#252526] rounded-lg">
          <FileCode2 className="w-3.5 h-3.5 text-blue-400 shrink-0" />
          <span className="text-xs text-[#aaa]">Contract:</span>
          <span className="text-xs font-mono text-white">{state.contractName}.sol</span>
        </div>
      )}

      {/* Success result */}
      {state.stage === 'complete' && (
        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <span className="text-xs font-semibold text-green-400">Contract Deployed!</span>
          </div>
          {state.contractAddress && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-[#888]">Address:</span>
              <code className="text-[10px] font-mono text-green-300 bg-green-500/10 px-1.5 py-0.5 rounded">
                {state.contractAddress}
              </code>
            </div>
          )}
          <div className="flex items-center gap-3 text-[9px] text-[#666]">
            <span>Network: {state.network}</span>
            {state.durationMs && <span>Time: {(state.durationMs / 1000).toFixed(1)}s</span>}
          </div>
        </div>
      )}

      {/* Error result */}
      {state.stage === 'error' && state.error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-3.5 h-3.5 text-red-400" />
            <span className="text-xs font-semibold text-red-400">Pipeline Failed</span>
          </div>
          <p className="text-[10px] text-red-300/80 break-all">{state.error}</p>
        </div>
      )}

      {/* Close button */}
      {(state.stage === 'complete' || state.stage === 'error') && (
        <button
          onClick={onClose}
          className={cn(
            'w-full py-2 rounded-lg text-xs font-medium transition-colors',
            state.stage === 'complete'
              ? 'bg-green-600 hover:bg-green-500 text-white'
              : 'bg-[#333] hover:bg-[#444] text-[#ccc]',
          )}
        >
          {state.stage === 'complete' ? 'Start Interacting →' : 'Close'}
        </button>
      )}
    </div>
  );
}
